import React, { useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Clear as ClearIcon,
  FilterList as FilterListIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import {
  STAGED_FILTER_FIELDS,
  StagedFilterKey,
  StagedFilterOption,
  StagedFilterState,
  StagedFilterValue,
  hasActiveStagedFilters,
  stagedFilterField,
  stagedFilterPillKeys,
  stagedFilterValueCount,
  stagedIssueFilterMode,
} from '../../utils/stagedFilterState';
import FilterPill from './FilterPill';
import AddFilterMenu from './AddFilterMenu';
import SavedViewsButton from './SavedViewsButton';

export interface TopIssueShortcut {
  /** The filter this shortcut drives — it is never state of its own. */
  key: StagedFilterKey;
  value: string;
  label: string;
  tone: 'critical' | 'warning' | 'ok';
  count?: number;
}

export interface StagedProductsFilterBarProps {
  filters: StagedFilterState;
  /** Brands present in the loaded queue; every other field's options are static. */
  brandOptions: StagedFilterOption[];
  onChangeValue: (key: StagedFilterKey, value: StagedFilterValue) => void;
  onAddFilter: (key: StagedFilterKey) => void;
  onRemoveFilter: (key: StagedFilterKey) => void;
  onClearAll: () => void;
  /** Search is held locally by the page so typing stays responsive; it is debounced into the query. */
  search: string;
  onSearchChange: (value: string) => void;
  /** Facet counts per field, computed for the current query minus that field. */
  optionCounts?: Partial<Record<StagedFilterKey, Record<string, number>>>;
  topIssues: TopIssueShortcut[];
  onApplyShortcut: (key: StagedFilterKey, value: string) => void;
  resultCount: number;
  totalCount?: number;
  /** Keeps the previous count on screen, dimmed, instead of flashing a spinner. */
  loading?: boolean;
  savedViewQuery: string;
  onApplySavedView: (query: string) => void;
  /** Table-level controls (density, rows per load) that are not part of the query. */
  trailing?: React.ReactNode;
  /** Below this breakpoint, pills/add-filter/top-issues/trailing collapse into a bottom sheet. */
  isMobile?: boolean;
  mobileSheetOpen?: boolean;
  onMobileSheetOpenChange?: (open: boolean) => void;
}

const TONE_COLOR: Record<TopIssueShortcut['tone'], string> = {
  critical: 'error.main',
  warning: 'warning.main',
  ok: 'success.main',
};

/**
 * The bar shows only the filters that are actually applied; everything else
 * lives behind `+ Add filter`. Its length is a readout of query complexity
 * rather than of the schema.
 */
export default function StagedProductsFilterBar({
  filters,
  brandOptions,
  onChangeValue,
  onAddFilter,
  onRemoveFilter,
  onClearAll,
  search,
  onSearchChange,
  optionCounts,
  topIssues,
  onApplyShortcut,
  resultCount,
  totalCount,
  loading,
  savedViewQuery,
  onApplySavedView,
  trailing,
  isMobile,
  mobileSheetOpen,
  onMobileSheetOpenChange,
}: StagedProductsFilterBarProps) {
  // Only one popover at a time: opening a pill closes the Add menu and vice versa.
  const [openMenu, setOpenMenu] = useState<StagedFilterKey | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  const pillKeys = useMemo(() => stagedFilterPillKeys(filters), [filters]);
  const addableFields = useMemo(
    () => STAGED_FILTER_FIELDS.filter((f) => !pillKeys.includes(f.key)),
    [pillKeys],
  );
  const optionsFor = (key: StagedFilterKey): StagedFilterOption[] =>
    key === 'brand' ? brandOptions : stagedFilterField(key).options;

  const hasActive = hasActiveStagedFilters(filters) || pillKeys.length > 0;

  const searchField = (
    <TextField
      size="small"
      placeholder="Search name, SKU, MPN, brand"
      value={search}
      onChange={(e) => onSearchChange(e.target.value)}
      sx={{ width: { xs: '100%', sm: 280 }, flex: isMobile ? '1 1 auto' : '0 0 auto' }}
      InputProps={{
        sx: { height: 34 },
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon fontSize="small" color="action" />
          </InputAdornment>
        ),
        endAdornment: search ? (
          <InputAdornment position="end">
            <IconButton size="small" aria-label="Clear search" onClick={() => onSearchChange('')}>
              <ClearIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        ) : undefined,
      }}
    />
  );

  const resultCountLabel = (
    <Typography
      aria-live="polite"
      variant="body2"
      sx={{
        fontSize: '0.78125rem',
        color: 'text.secondary',
        fontVariantNumeric: 'tabular-nums',
        opacity: loading ? 0.5 : 1,
        transition: 'opacity 0.15s',
        whiteSpace: 'nowrap',
      }}
    >
      <Box component="span" sx={{ color: 'text.primary', fontWeight: 500 }}>
        {resultCount}
      </Box>
      {totalCount != null ? ` of ${totalCount}` : ' shown'}
    </Typography>
  );

  // On mobile, everything except search collapses into one "Filters" button
  // (badged with the active-filter count) that opens a bottom sheet — pills,
  // add-filter, top issues, and the trailing table controls all live there
  // instead of competing for space in a single row.
  if (isMobile) {
    const activeCount = stagedFilterValueCount(filters);
    return (
      <>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.5 }}>
          {searchField}
          <Badge badgeContent={activeCount} color="primary" overlap="circular">
            <Button
              variant="outlined"
              size="small"
              startIcon={<FilterListIcon fontSize="small" />}
              onClick={() => onMobileSheetOpenChange?.(true)}
              sx={{ height: 34, whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              Filters
            </Button>
          </Badge>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, pb: 1.25 }}>
          {resultCountLabel}
        </Box>
        <Drawer
          anchor="bottom"
          open={!!mobileSheetOpen}
          onClose={() => {
            setOpenMenu(null);
            setAddOpen(false);
            onMobileSheetOpenChange?.(false);
          }}
          PaperProps={{ sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16, maxHeight: '82vh', display: 'flex', flexDirection: 'column' } }}
        >
          <Box sx={{ width: 36, height: 4, bgcolor: 'grey.300', borderRadius: 3, mx: 'auto', mt: 1.5, mb: 0.5, flexShrink: 0 }} />
          <Typography variant="subtitle1" sx={{ px: 2, py: 1, fontWeight: 600, flexShrink: 0 }}>
            Filters &amp; view options
          </Typography>
          <Box sx={{ overflowY: 'auto', px: 2, pb: 1 }}>
            <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 1, display: 'block', mb: 0.75 }}>
              Active filters
            </Typography>
            <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', mb: 1.5 }}>
              {pillKeys.map((key) => (
                <FilterPill
                  key={key}
                  field={stagedFilterField(key)}
                  options={optionsFor(key)}
                  value={filters[key]}
                  counts={optionCounts?.[key]}
                  open={openMenu === key}
                  onOpen={() => {
                    setOpenMenu(key);
                    setAddOpen(false);
                  }}
                  onClose={() => setOpenMenu((prev) => (prev === key ? null : prev))}
                  onChange={(value) => onChangeValue(key, value)}
                  onRemove={() => {
                    setOpenMenu((prev) => (prev === key ? null : prev));
                    onRemoveFilter(key);
                  }}
                />
              ))}
              <AddFilterMenu
                fields={addableFields}
                open={addOpen}
                onOpen={() => {
                  setAddOpen(true);
                  setOpenMenu(null);
                }}
                onClose={() => setAddOpen(false)}
                onAdd={(key) => {
                  setAddOpen(false);
                  onAddFilter(key);
                  setOpenMenu(key);
                }}
              />
            </Stack>

            {topIssues.length > 0 && (
              <>
                <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 1, display: 'block', mb: 0.75 }}>
                  Top issues
                </Typography>
                <Stack spacing={0.5} sx={{ mb: 1.5 }}>
                  {topIssues.map((issue) => {
                    const current = filters[issue.key];
                    const applied = Array.isArray(current)
                      ? issue.key === 'issues'
                        ? stagedIssueFilterMode(current, issue.value) !== 'off'
                        : current.includes(issue.value)
                      : current === issue.value;
                    return (
                      <Box
                        component="button"
                        type="button"
                        key={`${issue.key}:${issue.value}`}
                        onClick={() => onApplyShortcut(issue.key, issue.value)}
                        aria-pressed={applied}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          border: 'none',
                          background: applied ? 'action.selected' : 'none',
                          borderRadius: 1,
                          p: 1,
                          cursor: 'pointer',
                          font: 'inherit',
                          fontSize: '0.8125rem',
                          textAlign: 'left',
                          color: applied ? 'primary.dark' : 'text.primary',
                          fontWeight: applied ? 600 : 400,
                        }}
                      >
                        <Box
                          component="span"
                          sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: TONE_COLOR[issue.tone], flexShrink: 0 }}
                        />
                        <Box component="span" sx={{ flex: 1 }}>{issue.label}</Box>
                        {issue.count != null && (
                          <Box component="span" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                            {issue.count}
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Stack>
              </>
            )}

            <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 1, display: 'block', mb: 0.75 }}>
              Display
            </Typography>
            <Stack direction="row" spacing={1.5} alignItems="center" useFlexGap sx={{ flexWrap: 'wrap', mb: 1.5 }}>
              {trailing}
            </Stack>

            <Stack direction="row" spacing={1.5} alignItems="center">
              <SavedViewsButton currentQuery={savedViewQuery} onApply={onApplySavedView} />
              {hasActive && (
                <Button
                  size="small"
                  onClick={() => {
                    setOpenMenu(null);
                    setAddOpen(false);
                    onClearAll();
                  }}
                  sx={{ minWidth: 0, px: 0.5, fontSize: '0.78125rem', fontWeight: 500, color: 'text.secondary' }}
                >
                  Clear all
                </Button>
              )}
            </Stack>
          </Box>
          <Box sx={{ px: 2, py: 1.5, borderTop: 1, borderColor: 'divider', pb: 'calc(env(safe-area-inset-bottom, 0px) + 12px)', flexShrink: 0 }}>
            <Button
              variant="contained"
              fullWidth
              onClick={() => {
                setOpenMenu(null);
                setAddOpen(false);
                onMobileSheetOpenChange?.(false);
              }}
            >
              Apply
            </Button>
          </Box>
        </Drawer>
      </>
    );
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1.75,
          flexWrap: 'wrap',
        }}
      >
        {searchField}

        <Divider orientation="vertical" flexItem sx={{ height: 22, alignSelf: 'center', mx: 0.5 }} />

        {pillKeys.map((key) => (
          <FilterPill
            key={key}
            field={stagedFilterField(key)}
            options={optionsFor(key)}
            value={filters[key]}
            counts={optionCounts?.[key]}
            open={openMenu === key}
            onOpen={() => {
              setOpenMenu(key);
              setAddOpen(false);
            }}
            onClose={() => setOpenMenu((prev) => (prev === key ? null : prev))}
            onChange={(value) => onChangeValue(key, value)}
            onRemove={() => {
              setOpenMenu((prev) => (prev === key ? null : prev));
              onRemoveFilter(key);
            }}
          />
        ))}

        <AddFilterMenu
          fields={addableFields}
          open={addOpen}
          onOpen={() => {
            setAddOpen(true);
            setOpenMenu(null);
          }}
          onClose={() => setAddOpen(false)}
          onAdd={(key) => {
            setAddOpen(false);
            onAddFilter(key);
            // Adding a filter opens its value menu — one gesture, not two.
            setOpenMenu(key);
          }}
        />

        <Box sx={{ flex: '1 1 auto' }} />

        <Stack direction="row" spacing={1.5} alignItems="center" useFlexGap sx={{ flexWrap: 'wrap' }}>
          <Typography
            aria-live="polite"
            variant="body2"
            sx={{
              fontSize: '0.78125rem',
              color: 'text.secondary',
              fontVariantNumeric: 'tabular-nums',
              opacity: loading ? 0.5 : 1,
              transition: 'opacity 0.15s',
            }}
          >
            <Box component="span" sx={{ color: 'text.primary', fontWeight: 500 }}>
              {resultCount}
            </Box>
            {totalCount != null ? ` of ${totalCount}` : ' shown'}
          </Typography>
          {hasActive && (
            <Button
              size="small"
              onClick={() => {
                setOpenMenu(null);
                setAddOpen(false);
                onClearAll();
              }}
              sx={{ minWidth: 0, px: 0.5, fontSize: '0.78125rem', fontWeight: 500, color: 'text.secondary' }}
            >
              Clear
            </Button>
          )}
          <SavedViewsButton currentQuery={savedViewQuery} onApply={onApplySavedView} />
          {trailing}
        </Stack>
      </Box>

      {topIssues.length > 0 && (
        <Stack
          direction="row"
          spacing={2.25}
          alignItems="center"
          useFlexGap
          sx={{
            px: 2,
            py: 1.125,
            flexWrap: 'wrap',
            bgcolor: 'grey.50',
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontSize: '0.6875rem',
              fontWeight: 600,
              letterSpacing: '0.09em',
              textTransform: 'uppercase',
              color: 'text.disabled',
            }}
          >
            Top issues
          </Typography>
          {topIssues.map((issue) => {
            const current = filters[issue.key];
            const applied = Array.isArray(current)
              ? issue.key === 'issues'
                ? stagedIssueFilterMode(current, issue.value) !== 'off'
                : current.includes(issue.value)
              : current === issue.value;
            return (
              <Box
                component="button"
                type="button"
                key={`${issue.key}:${issue.value}`}
                onClick={() => onApplyShortcut(issue.key, issue.value)}
                aria-pressed={applied}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.75,
                  border: 'none',
                  background: 'none',
                  p: 0,
                  cursor: 'pointer',
                  font: 'inherit',
                  fontSize: '0.78125rem',
                  color: applied ? 'primary.dark' : 'text.secondary',
                  fontWeight: applied ? 500 : 400,
                  '&:hover': { color: 'text.primary' },
                }}
              >
                <Box
                  component="span"
                  sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: TONE_COLOR[issue.tone] }}
                />
                <span>{issue.label}</span>
                {issue.count != null && (
                  <Box
                    component="span"
                    sx={{ color: 'text.primary', fontVariantNumeric: 'tabular-nums' }}
                  >
                    {issue.count}
                  </Box>
                )}
              </Box>
            );
          })}
        </Stack>
      )}
    </>
  );
}
